import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ShieldStar, Lock, Envelope, User } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import usePageMeta from "@/hooks/usePageMeta";

export default function Register() {
  usePageMeta(
    "Commission Your Commander Account",
    "Forge your EIGOO Commander Account. Unlock the AI Blueprint Engine, download vault, and Sovereign Business Starters across 53 niches."
  );
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) {
      setErr("Access key must be at least 6 characters.");
      return;
    }
    setLoading(true);
    setErr("");
    const res = await register(form.email, form.password, form.name);
    setLoading(false);
    if (res.ok) {
      navigate("/commander");
    } else {
      setErr(res.error);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-6 py-16" data-testid="register-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <div className="w-14 h-14 border border-[#FFD700] rotate-45 flex items-center justify-center mx-auto mb-6 animate-pulse-gold">
            <ShieldStar size={26} weight="duotone" className="text-[#FFD700] -rotate-45" />
          </div>
          <span className="overline">// Fleet Commissioning</span>
          <h1 className="heading-display text-4xl mt-3 mb-2">Commission</h1>
          <p className="text-sm text-white/50 font-light">Forge your Commander Account.</p>
        </div>

        <form onSubmit={submit} className="surface-card p-8 space-y-5">
          <div>
            <Label className="overline text-[0.6rem] mb-2 block">Commander Designation</Label>
            <div className="relative">
              <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
              <Input
                data-testid="register-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                className="pl-10 bg-[#0c0c0c] border-white/10 rounded-none h-12 text-white"
                placeholder="Your name"
              />
            </div>
          </div>
          <div>
            <Label className="overline text-[0.6rem] mb-2 block">Email</Label>
            <div className="relative">
              <Envelope size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
              <Input
                data-testid="register-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
                className="pl-10 bg-[#0c0c0c] border-white/10 rounded-none h-12 text-white"
                placeholder="commander@fleet.net"
              />
            </div>
          </div>
          <div>
            <Label className="overline text-[0.6rem] mb-2 block">Access Key (min 6 chars)</Label>
            <div className="relative">
              <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
              <Input
                data-testid="register-password"
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required
                minLength={6}
                className="pl-10 bg-[#0c0c0c] border-white/10 rounded-none h-12 text-white"
                placeholder="••••••••••••"
              />
            </div>
          </div>

          {err && (
            <div data-testid="register-error" className="text-sm text-red-400 border border-red-900/40 bg-red-950/30 p-3 font-mono text-xs">
              {err}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="register-submit-btn"
            className="w-full btn-gold py-4 text-xs disabled:opacity-50"
          >
            {loading ? "Commissioning..." : "Commission Account"}
          </button>
        </form>

        <div className="mt-6 text-center text-sm text-white/50">
          Already commissioned?{" "}
          <Link to="/login" className="text-[#FFD700] hover:underline font-semibold" data-testid="link-to-login">
            Sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
